import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type Player, type Team } from '../../services/api';
import { toast } from '../shared/Toast';
import '../shared/ConfirmDialog';

@customElement('player-manager')
export class PlayerManager extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private players: Player[] = [];

  @state()
  private teams: Team[] = [];

  @state()
  private filterTeamId = '';

  @state()
  private loading = true;

  @state()
  private showForm = false;

  @state()
  private editingPlayer: Player | null = null;

  @state()
  private formName = '';

  @state()
  private formTeamId = '';

  @state()
  private formNumber: number | null = null;

  @state()
  private formPosition = '';

  @state()
  private formDob = '';

  @state()
  private saving = false;

  @state()
  private searchQuery = '';

  // Delete confirmation
  @state()
  private deleteConfirmOpen = false;

  @state()
  private deletingPlayer: Player | null = null;

  @state()
  private deleting = false;

  // Form validation
  @state()
  private formTouched = false;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  private async loadData() {
    this.loading = true;
    try {
      const [players, teams] = await Promise.all([
        api.getPlayers(this.filterTeamId || undefined),
        api.getTeams(),
      ]);
      this.players = (players || []).filter((p) => !p.is_adhoc); // Only show permanent players
      this.teams = teams || [];
    } catch (error) {
      console.error('Failed to load players:', error);
      toast.error('Failed to load players');
    } finally {
      this.loading = false;
    }
  }

  private get filteredPlayers(): Player[] {
    if (!this.searchQuery) return this.players;
    const query = this.searchQuery.toLowerCase();
    return this.players.filter(
      p => p.name.toLowerCase().includes(query) ||
           p.team_name?.toLowerCase().includes(query) ||
           p.jersey_number?.toString().includes(query) ||
           p.position?.toLowerCase().includes(query)
    );
  }

  private get formNameError(): string | null {
    if (!this.formTouched) return null;
    if (!this.formName.trim()) return 'Name is required';
    return null;
  }

  private get formTeamError(): string | null {
    if (!this.formTouched) return null;
    if (!this.formTeamId) return 'Team is required';
    return null;
  }

  private async handleFilterChange(teamId: string) {
    this.filterTeamId = teamId;
    await this.loadData();
  }

  private openForm(player?: Player) {
    this.editingPlayer = player || null;
    this.formName = player?.name || '';
    this.formTeamId = player?.team_id || '';
    this.formNumber = player?.jersey_number || null;
    this.formPosition = player?.position || '';
    // Extract date part from RFC3339 timestamp (e.g., "2010-05-15T00:00:00Z" -> "2010-05-15")
    this.formDob = player?.date_of_birth ? player.date_of_birth.split('T')[0] : '';
    this.showForm = true;
    this.formTouched = false;
  }

  private closeForm() {
    this.showForm = false;
    this.editingPlayer = null;
    this.formName = '';
    this.formTeamId = '';
    this.formNumber = null;
    this.formPosition = '';
    this.formTouched = false;
  }

  private async savePlayer() {
    this.formTouched = true;
    if (this.formNameError || this.formTeamError) return;

    this.saving = true;
    try {
      const data = {
        name: this.formName.trim(),
        team_id: this.formTeamId,
        jersey_number: this.formNumber || undefined,
        position: this.formPosition.trim() || undefined,
        is_adhoc: false,
        date_of_birth: this.formDob || undefined,
      };

      if (this.editingPlayer) {
        await api.updatePlayer(this.editingPlayer.id, data);
        toast.success(`Player "${this.formName}" updated`);
      } else {
        await api.createPlayer(data);
        toast.success(`Player "${this.formName}" created`);
      }
      await this.loadData();
      this.closeForm();
    } catch (error) {
      console.error('Failed to save player:', error);
      toast.error('Failed to save player');
    } finally {
      this.saving = false;
    }
  }

  private confirmDelete(player: Player) {
    this.deletingPlayer = player;
    this.deleteConfirmOpen = true;
  }

  private cancelDelete() {
    this.deleteConfirmOpen = false;
    this.deletingPlayer = null;
  }

  private async executeDelete() {
    if (!this.deletingPlayer) return;

    this.deleting = true;
    try {
      await api.deletePlayer(this.deletingPlayer.id);
      toast.success(`Player "${this.deletingPlayer.name}" deleted`);
      await this.loadData();
      this.cancelDelete();
    } catch (error) {
      console.error('Failed to delete player:', error);
      toast.error('Failed to delete player');
    } finally {
      this.deleting = false;
    }
  }

  render() {
    return html`
      <div class="space-y-4">
        <div class="flex flex-col gap-3">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold">Players (${this.players.length})</h3>
            <button class="btn btn-primary btn-sm whitespace-nowrap" @click=${() => this.openForm()}>
              Add Player
            </button>
          </div>
          <div class="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              class="input input-bordered input-sm flex-1 min-w-0"
              placeholder="Search..."
              .value=${this.searchQuery}
              @input=${(e: Event) => this.searchQuery = (e.target as HTMLInputElement).value}
            />
            <select
              class="select select-bordered sm:w-auto"
              .value=${this.filterTeamId}
              @change=${(e: Event) => this.handleFilterChange((e.target as HTMLSelectElement).value)}
            >
              <option value="">All Teams</option>
              ${this.teams.map((team) => html`
                <option value=${team.id}>${team.name}</option>
              `)}
            </select>
          </div>
        </div>

        <!-- Loading skeleton -->
        ${this.loading ? html`
          <div class="space-y-2">
            ${[1, 2, 3].map(() => html`
              <div class="skeleton h-12 w-full"></div>
            `)}
          </div>
        ` : html`
          <!-- Form -->
          ${this.showForm ? this.renderForm() : nothing}

          <!-- Players list -->
          ${this.renderPlayersList()}

          <!-- Empty state -->
          ${this.filteredPlayers.length === 0 ? html`
            <p class="text-center text-base-content/70 py-4">
              ${this.searchQuery
                ? 'No players match your search.'
                : this.filterTeamId
                  ? 'No players in this team.'
                  : 'No players yet. Create teams first, then add players.'}
            </p>
          ` : nothing}
        `}

        <!-- Delete confirmation dialog -->
        <confirm-dialog
          .open=${this.deleteConfirmOpen}
          title="Delete Player"
          .message=${`Delete "${this.deletingPlayer?.name}"? Their pitch history will also be deleted.`}
          confirmText="Delete"
          .destructive=${true}
          .loading=${this.deleting}
          @confirm=${this.executeDelete}
          @cancel=${this.cancelDelete}
        ></confirm-dialog>
      </div>
    `;
  }

  private renderForm() {
    return html`
      <div class="card bg-base-200">
        <div class="card-body">
          <h4 class="card-title">${this.editingPlayer ? 'Edit Player' : 'New Player'}</h4>
          <div class="grid gap-4 md:grid-cols-2">
            <div class="form-control">
              <label class="label"><span class="label-text">Name *</span></label>
              <input
                type="text"
                class="input input-bordered ${this.formNameError ? 'input-error' : ''}"
                placeholder="Enter player name"
                .value=${this.formName}
                @input=${(e: Event) => {
                  this.formName = (e.target as HTMLInputElement).value;
                  this.formTouched = true;
                }}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') this.savePlayer();
                  if (e.key === 'Escape') this.closeForm();
                }}
              />
              ${this.formNameError ? html`
                <label class="label">
                  <span class="label-text-alt text-error">${this.formNameError}</span>
                </label>
              ` : nothing}
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Team *</span></label>
              <select
                class="select select-bordered ${this.formTeamError ? 'select-error' : ''}"
                @change=${(e: Event) => {
                  this.formTeamId = (e.target as HTMLSelectElement).value;
                  this.formTouched = true;
                }}
              >
                <option value="" ?selected=${!this.formTeamId}>Select team</option>
                ${this.teams.map((team) => html`
                  <option value=${team.id} ?selected=${this.formTeamId === team.id}>${team.name}</option>
                `)}
              </select>
              ${this.formTeamError ? html`
                <label class="label">
                  <span class="label-text-alt text-error">${this.formTeamError}</span>
                </label>
              ` : nothing}
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Jersey Number</span></label>
              <input
                type="number"
                class="input input-bordered"
                min="0"
                max="99"
                placeholder="0-99"
                .value=${this.formNumber?.toString() ?? ''}
                @input=${(e: Event) => {
                  const val = (e.target as HTMLInputElement).value;
                  this.formNumber = val ? parseInt(val, 10) : null;
                }}
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Position</span></label>
              <input
                type="text"
                class="input input-bordered"
                placeholder="e.g., Pitcher, Catcher"
                .value=${this.formPosition}
                @input=${(e: Event) => this.formPosition = (e.target as HTMLInputElement).value}
              />
            </div>
            <div class="form-control">
              <label class="label">
                <span class="label-text">Date of Birth</span>
                <span class="label-text-alt text-info">For age-based pitch limits</span>
              </label>
              <input
                type="date"
                class="input input-bordered"
                .value=${this.formDob}
                @input=${(e: Event) => {
                  this.formDob = (e.target as HTMLInputElement).value;
                  this.formTouched = true;
                }}
              />
            </div>
          </div>
          <div class="card-actions justify-end mt-4">
            <button class="btn btn-ghost" @click=${this.closeForm}>Cancel</button>
            <button
              class="btn btn-primary"
              @click=${this.savePlayer}
              ?disabled=${this.saving}
            >
              ${this.saving ? html`<span class="loading loading-spinner loading-sm"></span>` : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderPlayersList() {
    if (this.filteredPlayers.length === 0) return nothing;

    return html`
      <div class="overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Team</th>
              <th>Position</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${this.filteredPlayers.map((player) => html`
              <tr class="hover">
                <td class="font-mono">${player.jersey_number ?? '-'}</td>
                <td class="font-semibold">${player.name}</td>
                <td class="text-base-content/70">${player.team_name || '-'}</td>
                <td class="text-base-content/70">${player.position || '-'}</td>
                <td class="text-right">
                  <button class="btn btn-ghost btn-xs" @click=${() => this.openForm(player)}>Edit</button>
                  <button class="btn btn-ghost btn-xs text-error" @click=${() => this.confirmDelete(player)}>Delete</button>
                </td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }
}
